import React from 'react';
import { Progress } from '@/components/ui/progress';
import { Card } from '@/components/ui/card';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface AutoMigrationIndicatorProps {
  isRunning: boolean;
  progress: number;
  total: number;
  completed: number;
  errors: string[];
  hasRun: boolean;
}

export const AutoMigrationIndicator: React.FC<AutoMigrationIndicatorProps> = ({
  isRunning,
  progress,
  total,
  completed,
  errors,
  hasRun
}) => {
  if (!isRunning && !hasRun && errors.length === 0) {
    return null;
  }

  return (
    <Card className="p-4 mb-4 border-primary/20">
      {isRunning && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="text-sm font-medium">Syncing courses to database...</span>
          </div>
          
          <Progress value={progress} className="h-2" />
          
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{Math.round(progress)}% complete</span>
            <span>{completed} of {total} courses</span>
          </div>
        </div>
      )}

      {hasRun && !isRunning && (
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <span className="text-green-600">
            Successfully synced {completed} courses to database
          </span>
        </div>
      )}

      {errors.length > 0 && (
        <Alert className="mt-3">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="text-sm">
              <strong>Migration issues:</strong>
              <ul className="mt-1 space-y-1">
                {errors.slice(0, 3).map((error, index) => (
                  <li key={index} className="text-xs text-muted-foreground">
                    • {error}
                  </li>
                ))}
              </ul>
              {errors.length > 3 && (
                <p className="text-xs text-muted-foreground mt-1">
                  And {errors.length - 3} more issues...
                </p>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}
    </Card>
  );
};